/* eslint-disable no-console */
import * as React from 'react';
import styled from 'styled-components';
import { ResponsivePie } from '@nivo/pie';

import { SelectInputWithSelectAll } from '../../atoms';
import { getFranchiseActivities } from '../../api/dashboard';
import { GET_ALL_QUERY_PARAMS } from '../../constants';
import { getUsers } from '../../api/users';
import { USER_TYPES_VALUES } from '../../constants/forms';

const PALETTE = [
  '#8DD3C7',
  '#FDB462',
  '#BEBADA',
  '#FB8072',
  '#80B1D3',
  '#FCCDE5',
  '#B3DE69',
  '#FFED6F',
];

const FATodayTaskWidget = () => {
  const [employees, setEmployees] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [
    selectedEmployees,
    setSelectedEmployees,
  ] = React.useState(null);

  React.useEffect(() => {
    // Show all employees on initial load
    setSelectedEmployees([]);

    getUsers({
      ...GET_ALL_QUERY_PARAMS,
      userRole: USER_TYPES_VALUES.EMPLOYEE,
    })
      .then(response => {
        const result = response.data.results.map(el => ({
          ...el,
          fullName: `${el.firstName} ${el.lastName}`,
        }));
        setEmployees(result);
      })
      .catch(e => console.error(e));
  }, []);

  React.useEffect(() => {
    getFranchiseActivities(selectedEmployees || [])
      .then(response => {
        const result = response.data.map(el => ({
          ...el,
          id: el.title,
          value: el.count,
        }));
        setData(result);
      })

      .catch(e => console.error(e));
  }, [selectedEmployees]);

  const onSelectChange = (
    selected: any[],
    isCanceled: any,
  ) => {
    if (!isCanceled) {
      setSelectedEmployees(
        selected?.map((item: { id: any }) => item.id),
      );
    }
  };

  const sortedData = React.useMemo(
    () =>
      [...(data || [])].sort((a, b) => b.value - a.value),
    [data],
  );

  const total = React.useMemo(
    () => sortedData.reduce((sum, item) => sum + item.value, 0),
    [sortedData],
  );

  return (
    <Wrapper>
      <Header>Today&apos;s Tasks</Header>
      {employees && employees?.length && (
        <SelectContainer>
          <SelectInputWithSelectAll
            data={employees}
            valueKey="id"
            labelKey="fullName"
            onMenuClose={onSelectChange}
            placeholder="Select Employees from the list..."
          />
        </SelectContainer>
      )}
      {sortedData.length > 0 && (
        <>
          <PieHolder>
            <ResponsivePie
              data={sortedData}
              colors={PALETTE}
              margin={{
                top: 16,
                right: 16,
                bottom: 16,
                left: 16,
              }}
              innerRadius={0.65}
              padAngle={1}
              cornerRadius={3}
              borderWidth={1}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 0.2]],
              }}
              enableRadialLabels={false}
              sliceLabelsSkipAngle={12}
              sliceLabelsTextColor="#1f2937"
            />
            <PieCenter>
              <PieCenterLabel>Total</PieCenterLabel>
              <PieCenterValue>{total}</PieCenterValue>
            </PieCenter>
          </PieHolder>
          <Legend>
            {sortedData.map((item, i) => (
              <LegendItem key={item.id}>
                <LegendDot
                  style={{
                    background: PALETTE[i % PALETTE.length],
                  }}
                />
                <LegendName title={item.id}>
                  {item.id}
                </LegendName>
                <LegendValue>{item.value}</LegendValue>
              </LegendItem>
            ))}
          </Legend>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const SelectContainer = styled.div`
  margin: 0 0 16px;
`;

const PieHolder = styled.div`
  position: relative;
  width: 100%;
  height: 280px;
`;

const PieCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PieCenterLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const PieCenterValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.2;
`;

const Legend = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(140px, 1fr)
  );
  gap: 8px 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 0.8125rem;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const LegendName = styled.span`
  color: var(--neutral-700);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const LegendValue = styled.span`
  color: var(--mainText);
  font-weight: 600;
  flex-shrink: 0;
`;

const Header = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

export default FATodayTaskWidget;
